import React, { useState, useEffect } from 'react';
import { fetchDiaryEntry, getAllTopicsExercisesStatus, saveDiaryEntry } from '../services/api';
import DiaryNoteForm from './diary/DiaryNoteForm';
import { TOPIC_TITLES } from '../data/builtInLessons';

// Only 'conversation' (Dialogy) has per-dialog content in the DB. conversation-fillin and
// speaking are topic-scoped — showing a dialog picker for them would be a lie.
const DIALOGUE_SCOPED_EXERCISE_IDS = new Set(['conversation', 'listening']);
// Dynamic lessons only support exercise types we can derive from / lazily generate against
// the conversation itself.
const DYNAMIC_LESSON_EXERCISE_IDS = new Set(['conversation', 'conversation-fillin', 'vocabulary']);

const ExerciseMenu = ({ onStartExercise, onBack, selectedTopic, selectedDialogueNumber = 1, selectedConversationId = null }) => {
  const isDynamic = selectedConversationId != null;
  const [exercisesStatus, setExercisesStatus] = useState(null);
  const [diaryNote, setDiaryNote] = useState('');
  const [diaryLoaded, setDiaryLoaded] = useState(false);
  const [diarySaving, setDiarySaving] = useState(false);
  const [diarySaved, setDiarySaved] = useState(false);
  const [showDiaryModal, setShowDiaryModal] = useState(false);


  // Map exercise types to backend exercise_type names
  const exerciseTypeMap = {
    'conversation': 'conversation_writing',
    'listening': 'conversation_listening',
    'conversation-fillin': 'conversation_fillin',
    'vocabulary': 'vocabulary_fillin',
    'speaking': 'speaking',
    'writing': 'writing',
    'ai-conversation': 'ai_conversation'
  };

  const exercises = [
    {
      id: 'conversation',
      name: 'Dialogy',
      desc: 'Přečtěte si konverzaci s audio',
      icon: '💬',
      color: 'blue',
      required: false
    },
    {
      id: 'listening',
      name: 'Poslech',
      desc: 'Nejdřív poslouchej, pak odpověz',
      icon: '🎧',
      color: 'cyan',
      required: false
    },
    {
      id: 'conversation-fillin',
      name: 'Doplňování',
      desc: 'Doplňte chybějící slova v odpovědích',
      icon: '📝',
      color: 'teal',
      required: false
    },
    {
      id: 'speaking',
      name: 'Mluvení',
      desc: 'Procvičte si mluvení nahlas',
      icon: '🎤',
      color: 'cyan',
      required: false
    },
    {
      id: 'vocabulary',
      name: 'Slovíčka',
      desc: 'Doplňte správné slovíčko do věty',
      icon: '⚡',
      color: 'amber',
      required: false
    },
    {
      id: 'ai-conversation',
      name: 'Konverzace s AI',
      desc: 'Povídejte si s AI nahlas anglicky',
      icon: '🗣️',
      color: 'purple',
      required: false
    },
    {
      id: 'writing',
      name: 'Psaní',
      desc: 'Napište text podle zadání',
      icon: '✏️',
      color: 'rose',
      required: true
    }
  ];

  // Fetch exercises status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await getAllTopicsExercisesStatus();
        setExercisesStatus(data.topics);
      } catch (error) {
        console.error('Error fetching exercises status:', error);
      }
    };

    fetchStatus();
  }, []);

  useEffect(() => {
    const loadDiaryNote = async () => {
      try {
        const entry = await fetchDiaryEntry(selectedTopic, 'lesson_overview');
        if (entry && entry.note) {
          setDiaryNote(entry.note);
          setDiarySaved(true);
        } else {
          setDiaryNote('');
          setDiarySaved(false);
        }
      } catch (error) {
        console.error('Error fetching diary note:', error);
      } finally {
        setDiaryLoaded(true);
      }
    };

    loadDiaryNote();
  }, [selectedTopic]);

  const getExerciseInfo = (exerciseType) => {
    if (!exercisesStatus || !exercisesStatus[selectedTopic]) return null;
    const backendExerciseType = exerciseTypeMap[exerciseType];
    return exercisesStatus[selectedTopic].exercises.find(ex => ex.exercise_type === backendExerciseType) || null;
  };

  // Helper function to get exercise status: 'not_started', 'in_progress', or 'finished'
  const getExerciseStatus = (exerciseType) => {
    const exerciseInfo = getExerciseInfo(exerciseType);
    if (!exerciseInfo) return 'not_started';

    if (exerciseInfo.finished) {
      return 'finished';
    }
    if (exerciseInfo.attempts_count > 0 || exerciseInfo.has_progress) {
      return 'in_progress';
    }
    return 'not_started';
  };

  const getDialogues = (exerciseType) => {
    const info = getExerciseInfo(exerciseType);
    return info && Array.isArray(info.dialogues) ? info.dialogues : [];
  };

  const getDialogueStatus = (dialogue) => {
    if (dialogue.finished) return 'finished';
    if (dialogue.attempts_count > 0 || dialogue.has_progress) return 'in_progress';
    return 'not_started';
  };

  const getCategoryStatus = (exercise) => {
    if (!DIALOGUE_SCOPED_EXERCISE_IDS.has(exercise.id)) {
      return getExerciseStatus(exercise.id);
    }
    const dialogues = getDialogues(exercise.id);
    // Dynamic lessons are scoped to a single conversation, so the category status
    // mirrors that one dialogue's status (not an aggregate across the topic).
    if (isDynamic) {
      const dialogue = dialogues.find(d => d.conversation_id === selectedConversationId);
      return dialogue ? getDialogueStatus(dialogue) : 'not_started';
    }
    if (dialogues.length === 0) return getExerciseStatus(exercise.id);
    const statuses = dialogues.map(getDialogueStatus);
    if (statuses.every(s => s === 'finished')) return 'finished';
    if (statuses.some(s => s !== 'not_started')) return 'in_progress';
    return 'not_started';
  };

  const getDialogueProgressLabel = (exercise) => {
    const dialogues = getDialogues(exercise.id);
    if (dialogues.length === 0) return null;
    const done = dialogues.filter(d => d.finished).length;
    return `${done}/${dialogues.length}`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finished':
        return (
          <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'in_progress':
        return (
          <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full"></div>
          </div>
        );
      default:
        return (
          <div className="w-7 h-7 rounded-full border-2 border-gray-300 flex items-center justify-center">
            <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          </div>
        );
    }
  };

  const getColorClasses = (color, required) => {
    if (required) {
      return {
        bg: 'bg-rose-500',
        hover: 'hover:bg-rose-600',
        text: 'text-white',
        subtext: 'text-rose-100'
      };
    }
    const colors = {
      purple: { bg: 'bg-white', hover: 'hover:bg-purple-50', border: 'border-l-4 border-purple-500' },
      amber: { bg: 'bg-white', hover: 'hover:bg-amber-50', border: 'border-l-4 border-amber-500' },
      blue: { bg: 'bg-white', hover: 'hover:bg-blue-50', border: 'border-l-4 border-blue-500' },
      teal: { bg: 'bg-white', hover: 'hover:bg-teal-50', border: 'border-l-4 border-teal-500' },
      cyan: { bg: 'bg-white', hover: 'hover:bg-cyan-50', border: 'border-l-4 border-cyan-500' },
    };
    return colors[color] || colors.purple;
  };

  const getIconBgClass = (color) => {
    const colors = {
      purple: 'bg-purple-100',
      amber: 'bg-amber-100',
      blue: 'bg-blue-100',
      teal: 'bg-teal-100',
      cyan: 'bg-cyan-100',
      rose: 'bg-white/20'
    };
    return colors[color] || 'bg-gray-100';
  };

  const topicStatus = exercisesStatus && exercisesStatus[selectedTopic];

  // For dynamic lessons, only show types that work without seeded per-topic data.
  // Writing is hidden because we don't lazily generate writing prompts (yet).
  const visibleExercises = isDynamic
    ? exercises.filter(e => DYNAMIC_LESSON_EXERCISE_IDS.has(e.id))
    : exercises;
  const optionalExercises = visibleExercises.filter(e => !e.required);
  const requiredExercise = visibleExercises.find(e => e.required);

  // In dynamic mode the topic-level totals from the backend may also include
  // seeded exercises (when a dynamic topic name collides with a seeded one),
  // so compute progress from the visible tiles to keep the ring honest.
  const completedCount = isDynamic
    ? visibleExercises.filter(e => getCategoryStatus(e) === 'finished').length
    : (topicStatus ? topicStatus.completed_exercises : 0);
  const totalCount = isDynamic
    ? visibleExercises.length
    : (topicStatus ? topicStatus.total_exercises : exercises.length);
  const progressFraction = totalCount > 0 ? completedCount / totalCount : 0;

  const handleExerciseClick = (exerciseId, options = {}) => {
    // For dynamic lessons every exercise is scoped to the same conversation.
    // Default the IDs from props so handlers without explicit options still work.
    const merged = isDynamic
      ? {
          dialogueNumber: options.dialogueNumber ?? selectedDialogueNumber,
          conversationId: options.conversationId ?? selectedConversationId,
        }
      : options;
    onStartExercise(exerciseId, merged);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-gray-200 p-4 md:p-6">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Zpět na lekce</span>
        </button>

        {/* Title Card */}
        <div className="bg-white rounded-2xl shadow-lg p-5 mb-6">
          <h1 className="text-xl font-bold text-gray-800 text-center mb-1">
            {TOPIC_TITLES[selectedTopic] || selectedTopic}
          </h1>

          {/* Circular Progress */}
          <div className="flex items-center justify-center gap-6 mt-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle cx="40" cy="40" r="35" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                <circle
                  cx="40" cy="40" r="35"
                  stroke="#22c55e"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${progressFraction * 220} 220`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-800">{completedCount}/{totalCount}</span>
              </div>
            </div>
            <div className="text-left">
              <p className="text-gray-500 text-sm">Dokončeno</p>
              <p className="text-lg font-semibold text-gray-700">{completedCount} ze {totalCount} cvičení</p>
            </div>
          </div>
        </div>

        <div className="flex justify-center mb-6">
          <button
            type="button"
            onClick={() => setShowDiaryModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gray-700 hover:bg-gray-800 text-white shadow-md hover:shadow-lg active:scale-95 transition-all"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Můj deník
            {diarySaved && (
              <span className="ml-1 inline-block w-2 h-2 rounded-full bg-green-400" />
            )}
          </button>
        </div>

        <p className="text-center text-lg text-gray-600 font-medium mt-10 mb-4">Tato cvičení jsou dobrovolná</p>

        {/* Optional Exercises - First */}
        <div className="mb-2 px-1">
          <span className="text-gray-500 font-medium text-sm uppercase tracking-wide">
            Volitelné
          </span>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-4">
          {optionalExercises.map((exercise, idx, arr) => {
            const isDialogueScoped = DIALOGUE_SCOPED_EXERCISE_IDS.has(exercise.id);
            const dialogues = isDialogueScoped ? getDialogues(exercise.id) : [];
            const hasDialogues = dialogues.length > 0;
            // Dynamic lessons are scoped to one conversation, so we skip the
            // dialogue picker and the "x/y" progress label.
            const showDialoguePicker = isDialogueScoped && hasDialogues && !isDynamic;
            const progressLabel = isDialogueScoped && !isDynamic ? getDialogueProgressLabel(exercise) : null;

            return (
              <div key={exercise.id}>
                <button
                  onClick={() => {
                    if (!showDialoguePicker) {
                      handleExerciseClick(exercise.id);
                    }
                  }}
                  className={`w-full p-4 ${getColorClasses(exercise.color, false).border} ${showDialoguePicker ? '' : getColorClasses(exercise.color, false).hover} transition-colors text-left flex items-center gap-4 ${showDialoguePicker ? 'cursor-default' : ''}`}
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-2xl ${getIconBgClass(exercise.color)}`}>
                    {exercise.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-800 text-lg">{exercise.name}</h3>
                      {progressLabel && (
                        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {progressLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-500 text-sm truncate">{exercise.desc}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(getCategoryStatus(exercise))}
                  </div>
                </button>

                {showDialoguePicker && (
                  <div className="bg-gray-50">
                    {dialogues.map((dialogue) => (
                      <button
                        key={dialogue.conversation_id}
                        onClick={() => handleExerciseClick(exercise.id, {
                          dialogueNumber: dialogue.dialogue_number,
                          conversationId: dialogue.conversation_id,
                        })}
                        className="w-full p-3 pl-16 flex items-center justify-between hover:bg-gray-100 transition-colors text-left border-t border-gray-100"
                      >
                        <span className="text-gray-700">Dialog {dialogue.dialogue_number}</span>
                        {getStatusIcon(getDialogueStatus(dialogue))}
                      </button>
                    ))}
                  </div>
                )}

                {idx < arr.length - 1 && <div className="h-px bg-gray-100 ml-4"></div>}
              </div>
            );
          })}
        </div>

        <p className="text-center text-lg text-gray-600 font-medium mt-10 mb-4">Pro odemknutí další lekce musíte dokončit toto cvičení.</p>

        {/* Required Exercise - At the bottom */}
        {requiredExercise && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2 px-1">
              <span className="text-rose-500 font-bold text-sm uppercase tracking-wide">
                ★ Povinné
              </span>
            </div>
            <button
              onClick={() => handleExerciseClick(requiredExercise.id)}
              className={`w-full rounded-2xl shadow-lg p-5 ${getColorClasses(requiredExercise.color, true).bg} ${getColorClasses(requiredExercise.color, true).hover} transition-all transform hover:scale-[1.02] text-left`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-3xl">
                  {requiredExercise.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">{requiredExercise.name}</h3>
                  <p className="text-rose-100 text-sm">{requiredExercise.desc}</p>
                </div>
                <div className="flex flex-col items-center">
                  {getExerciseStatus(requiredExercise.id) === 'not_started' ? (
                    <span className="bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Začít →
                    </span>
                  ) : (
                    getStatusIcon(getExerciseStatus(requiredExercise.id))
                  )}
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Quick Legend */}
        <div className="mt-5 flex justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-green-500"></div>
            <span className="text-gray-500">Hotovo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-amber-500"></div>
            <span className="text-gray-500">Rozpracováno</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
            <span className="text-gray-500">Nezačato</span>
          </div>
        </div>
      </div>

      {showDiaryModal && (
        <div
          onClick={() => !diarySaving && setShowDiaryModal(false)}
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl my-auto relative"
          >
            <button
              type="button"
              onClick={() => !diarySaving && setShowDiaryModal(false)}
              disabled={diarySaving}
              aria-label="Zavřít"
              className="absolute -top-2 -right-2 z-10 w-9 h-9 rounded-full bg-white text-gray-700 shadow-lg hover:bg-gray-100 flex items-center justify-center disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <DiaryNoteForm
              title="Můj deník k této lekci"
              note={diaryNote}
              onNoteChange={setDiaryNote}
              onSave={async () => {
                if (!diaryNote.trim()) {
                  return;
                }
                try {
                  setDiarySaving(true);
                  await saveDiaryEntry({
                    topic: selectedTopic,
                    noteType: 'lesson_overview',
                    note: diaryNote.trim()
                  });
                  setDiarySaved(true);
                } catch (error) {
                  console.error('Error saving diary note:', error);
                } finally {
                  setDiarySaving(false);
                }
              }}
              saveLabel={diaryLoaded ? 'Uložit poznámku' : 'Načítám...'}
              inputDisabled={!diaryLoaded || diarySaving}
              buttonDisabled={!diaryLoaded || diarySaving || !diaryNote.trim()}
              statusMessage={diarySaved ? 'Poznámka uložena.' : ''}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseMenu;
